import { Entity, formatTicker } from '@api';
import { Alert, AlertType } from '@design/components/alert/Alert';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { Toggle } from '@design/components/toggle/Toggle';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { SendAllAssetsTo } from '@terminal/components/send-all-assets-to/SendAllAssetsTo';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { getActiveFilterOptions } from '@terminal/hooks/use-filter-actions/use-filter-actions-utils';
import { ResetAllButton } from '@terminal/views/screener/reset-all-button/ResetAllButton';
import { ResultsEmptyState } from '@terminal/views/screener/results-empty-state/ResultsEmptyState';
import { usePerformanceStore } from '@terminal/views/theme-overview/tab-content/performance/hooks/usePerformanceStore';
import { PerformanceChart } from '@terminal/views/theme-overview/tab-content/performance/performance-chart/PerformanceChart';
import { PerformanceFilters } from '@terminal/views/theme-overview/tab-content/performance/performance-filters/PerformanceFilters';
import { PerformanceTable } from '@terminal/views/theme-overview/tab-content/performance/performance-table/PerformanceTable';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MSPerformance.module.scss';
import { usePerformanceFiltersData } from './usePerformanceFiltersData';

export const MSPerformance = ({ theme }: { theme: any }) => {
  const { t } = useTranslation(['widget']);
  const [resetKeys, setResetKeys] = useState(0);
  const {
    showBenchmark,
    showBreakdown,
    breakdownDisabled,
    displayedEntityTags,
    filterGroups,
    setShowBenchmark,
    setShowBreakdown,
    clearAllActiveFilterOptions,
  } = usePerformanceStore(state => ({
    showBenchmark: state.showBenchmark,
    showBreakdown: state.showBreakdown,
    breakdownDisabled: state.breakdownDisabled,
    displayedEntityTags: state.displayedEntityTags,
    filterGroups: state.filterGroups,
    setShowBenchmark: state.setShowBenchmark,
    setShowBreakdown: state.setShowBreakdown,
    clearAllActiveFilterOptions: state.clearAllActiveFilterOptions,
  }));
  const tags = theme.constituents
    .map((constituent: any) => constituent.entity_tag)
    .slice(0, 10);
  const { themeData, entityTags, isFetching, missingEntities } =
    usePerformanceFiltersData({ tags });
  const isContentLoading = isFetching;

  useEffect(() => {
    if (breakdownDisabled) {
      setShowBreakdown(false);
    }
  }, [breakdownDisabled]);

  const stringifiedActiveFilterValues = useMemo(
    () => JSON.stringify(getActiveFilterOptions(filterGroups).sort()),
    [filterGroups]
  );

  useEffect(() => {
    setResetKeys(prev => prev + 1);
  }, [stringifiedActiveFilterValues]);

  const formatTickerList = (items: Entity[]): string => {
    const len = items.length;

    if (len === 1) {
      return formatTicker(items[0]);
    }

    if (len === 2) {
      return t('widget:themeOverview.missingAssets.two', {
        a: formatTicker(items[0]),
        b: formatTicker(items[1]),
      });
    }

    const allButLast = items
      .slice(0, -1)
      .map(item => formatTicker(item))
      .join(', ');
    const last = formatTicker(items[items.length - 1]);
    return t('widget:themeOverview.missingAssets.many', {
      items: allButLast,
      last,
    });
  };

  if (isContentLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '480px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.headerContainer}
        data-testId="to-performance-header"
      >
        <TooltipContainer
          inPortal
          label={t('widget:themeOverview.themePerformanceOver12mTooltip')}
          trigger={[TooltipTrigger.Hover]}
        >
          <h2 className={styles.title}>
            {t('widget:themeOverview.themePerformanceOver12m')}
          </h2>
        </TooltipContainer>
        <div className={styles.togglesWrapper}>
          <Toggle
            isChecked={showBenchmark}
            disabled={!themeData?.aggregate.length}
            onToggle={checked => setShowBenchmark(checked)}
            leftLabel={
              <div className={styles.benchmarkLabelWrapper}>
                {showBenchmark && (
                  <span className={styles.benchmarkLineLegend} aria-hidden />
                )}
                {t('widget:themeOverview.benchmark', { name: 'SPX' })}
              </div>
            }
          />

          <TooltipContainer
            label={
              breakdownDisabled
                ? t('widget:themeOverview.breakdownDisabled', {
                    max: 10,
                  })
                : undefined
            }
            data-testid="breakdown-disabled-tooltip"
          >
            <span>
              <Toggle
                isChecked={showBreakdown}
                onToggle={checked => setShowBreakdown(checked)}
                leftLabel={t('widget:themeOverview.breakdown')}
                disabled={breakdownDisabled || !themeData?.aggregate.length}
              />
            </span>
          </TooltipContainer>
        </div>
      </div>
      <PerformanceFilters />
      {!!missingEntities.length && !isFetching && (
        <div className={styles.alertContainer}>
          <Alert
            className={styles.alert}
            variant="inline"
            type={AlertType.Info}
            title={t('widget:themeOverview.issueImportingAsset', {
              asset: formatTickerList(missingEntities),
              count: entityTags.length - missingEntities.length,
              total: entityTags.length,
            })}
          />
        </div>
      )}
      {!!themeData?.aggregate.length || isFetching ? (
        <>
          <ErrorBoundaryWrapper resetKeys={[resetKeys]}>
            <PerformanceChart />
          </ErrorBoundaryWrapper>
          {!!entityTags.length && (
            <>
              <div className={styles.toolbarContainer}>
                <p className={styles.companyCount}>
                  {t('widget:themeOverview.companiesCount', {
                    count: displayedEntityTags?.length ?? 0,
                    total: entityTags.length,
                  })}
                </p>
                <SendAllAssetsTo
                  entityTags={displayedEntityTags}
                  source={'theme_overview'}
                />
              </div>
            </>
          )}
          <ErrorBoundaryWrapper resetKeys={[resetKeys]}>
            <PerformanceTable />
          </ErrorBoundaryWrapper>
        </>
      ) : (
        <ResultsEmptyState
          resetButton={
            <ResetAllButton
              resetActiveFilterOptions={() => clearAllActiveFilterOptions(t)}
            />
          }
        />
      )}
    </div>
  );
};
